import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type ConfigFormData = {
    successTitleDe: string;
    successTitleEn: string;
    successMessageDe: string;
    successMessageEn: string;
    emailConfirmationTextDe: string;
    emailConfirmationTextEn: string;
    appointmentDetailsTitleDe: string;
    appointmentDetailsTitleEn: string;
    addToCalendarButtonTextDe: string;
    addToCalendarButtonTextEn: string;
    returnHomeButtonTextDe: string;
    returnHomeButtonTextEn: string;
    // New fields
    progressDisplayDe: string;
    progressDisplayEn: string;
};

const ConfirmationCMS = () => {
    const [formData, setFormData] = useState<ConfigFormData>({
        successTitleDe: '',
        successTitleEn: '',
        successMessageDe: '',
        successMessageEn: '',
        emailConfirmationTextDe: '',
        emailConfirmationTextEn: '',
        appointmentDetailsTitleDe: '',
        appointmentDetailsTitleEn: '',
        addToCalendarButtonTextDe: '',
        addToCalendarButtonTextEn: '',
        returnHomeButtonTextDe: '',
        returnHomeButtonTextEn: '',
        // New fields
        progressDisplayDe: '',
        progressDisplayEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();
    const { data: configData, isLoading } = trpc.bookingFunnelCMS.getConfirmationConfig.useQuery();

    const updateMutation = trpc.bookingFunnelCMS.updateConfirmationConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getConfirmationConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                successTitleDe: config.successTitleDe,
                successTitleEn: config.successTitleEn,
                successMessageDe: config.successMessageDe,
                successMessageEn: config.successMessageEn,
                emailConfirmationTextDe: config.emailConfirmationTextDe,
                emailConfirmationTextEn: config.emailConfirmationTextEn,
                appointmentDetailsTitleDe: config.appointmentDetailsTitleDe,
                appointmentDetailsTitleEn: config.appointmentDetailsTitleEn,
                addToCalendarButtonTextDe: config.addToCalendarButtonTextDe,
                addToCalendarButtonTextEn: config.addToCalendarButtonTextEn,
                returnHomeButtonTextDe: config.returnHomeButtonTextDe,
                returnHomeButtonTextEn: config.returnHomeButtonTextEn,
                // New fields
                progressDisplayDe: config.progressDisplayDe ?? '',
                progressDisplayEn: config.progressDisplayEn ?? '',
            });
        }
    }, [configData]);

    const handleChange = (field: keyof ConfigFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateMutation.mutate(formData);
    };

    if (isLoading) {
        return <Skeleton className='h-96 w-full' />;
    }

    const renderFieldPair = (labelDe: string, labelEn: string, fieldDe: keyof ConfigFormData, fieldEn: keyof ConfigFormData, multiline = false) => (
        <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
                <Label>{labelDe}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                    />
                )}
            </div>
            <div className='space-y-2'>
                <Label>{labelEn}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>Bestätigung CMS</CardTitle>
                <CardDescription>
                    Verwalten Sie die Texte und Beschriftungen für die Buchungsbestätigungs-Seite.
                </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
                <div className='flex justify-end'>
                    <Button onClick={handleSave} disabled={!isDirty || updateMutation.isPending}>
                        {updateMutation.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                            <Save className='mr-2 h-4 w-4' />
                        )}
                        Speichern
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Fortschrittsanzeige</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Fortschrittsanzeige (DE)', 'Progress Display (EN)', 'progressDisplayDe', 'progressDisplayEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Erfolgsmeldung</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Erfolgs-Titel (DE)', 'Success Title (EN)', 'successTitleDe', 'successTitleEn')}
                        {renderFieldPair('Erfolgs-Nachricht (DE)', 'Success Message (EN)', 'successMessageDe', 'successMessageEn', true)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>E-Mail Bestätigung</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('E-Mail Bestätigung Text (DE)', 'Email Confirmation Text (EN)', 'emailConfirmationTextDe', 'emailConfirmationTextEn', true)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Termindetails</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Termindetails Titel (DE)', 'Appointment Details Title (EN)', 'appointmentDetailsTitleDe', 'appointmentDetailsTitleEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Buttons</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Zum Kalender hinzufügen (DE)', 'Add to Calendar (EN)', 'addToCalendarButtonTextDe', 'addToCalendarButtonTextEn')}
                        {renderFieldPair('Zurück zur Startseite (DE)', 'Return Home (EN)', 'returnHomeButtonTextDe', 'returnHomeButtonTextEn')}
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
};

export default ConfirmationCMS;
